import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, Check } from "lucide-react";
import { relativeMinutes } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

export default function NotificationsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/notifications");
            setItems(data || []);
        } catch (error) {
            console.warn("[notifications] load failed", error?.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const readAll = async () => {
        try {
            await api.post("/notifications/read-all");
            load();
        } catch (error) {
            console.warn("[notifications] mark-all failed", error?.message);
            toast.error("Could not update notifications");
        }
    };

    return (
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <h1 className="font-display text-3xl">Notifications</h1>
                    <p className="text-sm text-muted-foreground">Reservation confirmations, kitchen updates, and receipts.</p>
                </div>
                <Button variant="outline" onClick={readAll} data-testid="notifications-page-mark-all"><Check className="h-4 w-4 mr-1" /> Mark all read</Button>
            </div>
            {loading ? <Skeleton className="h-64" /> : items.length === 0 ? (
                <Card><CardContent className="p-10 text-center">
                    <Bell className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="font-medium">No notifications yet</p>
                </CardContent></Card>
            ) : (
                <div className="space-y-2" data-testid="notifications-page-list">
                    {items.map(n => (
                        <Card key={n.id} className={`overflow-hidden ${!n.read ? "border-l-4 border-l-[hsl(var(--brand-terracotta))]" : ""}`} data-testid="notification-page-item">
                            <CardContent className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="font-medium">{n.title}</div>
                                        <div className="text-sm text-muted-foreground">{n.body}</div>
                                    </div>
                                    <div className="text-xs text-muted-foreground whitespace-nowrap">{relativeMinutes(n.created_at)}</div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
